import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { baseRoute as artworkBaseRoute } from "./artwork";

const baseRoute = "back/api/artworks/history";

const _artworkHistorySchema = z.object({
  id: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueNom: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueVille: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueDates: z.string(),
  oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere: z.boolean(),
  artworkId: z.string(),
  updatedAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedById: z.string().optional(),
  createdById: z.string().optional(),
});

export type ArtworkHistory = z.infer<typeof _artworkHistorySchema>;

async function getHistoryOfArtwork(artworkId: string) {
  const { data } = await api.get<ArtworkHistory[]>(
    `${artworkBaseRoute}/${artworkId}/history`,
  );
  return data;
}

export function useGetHistoryOfArtwork(artworkId: string) {
  return useQuery({
    queryKey: ["artwork", artworkId, "history"],
    queryFn: () => getHistoryOfArtwork(artworkId),
  });
}

const _createArtworkHistorySchema = _artworkHistorySchema.omit({
  id: true,
  artworkId: true,
  createdAt: true,
  createdById: true,
  updatedAt: true,
  updatedById: true,
});

export type CreateArtworkHistory = z.infer<typeof _createArtworkHistorySchema>;

async function createArtworkHistory({
  history,
  artworkId,
}: {
  history: CreateArtworkHistory;
  artworkId: string;
}) {
  const { data } = await api.post(
    `${artworkBaseRoute}/${artworkId}/history`,
    history,
  );
  return data;
}

export function useCreateArtworkHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createArtworkHistory,
    onSuccess: (_, { artworkId }) => {
      queryClient.invalidateQueries({
        queryKey: ["artwork", artworkId, "history"],
      });
    },
  });
}

export type EditArtworkHistory = CreateArtworkHistory;

async function editArtworkHistory({
  history,
  historyId,
}: {
  history: EditArtworkHistory;
  historyId: string;
  artworkId: string;
}) {
  const { data } = await api.patch(`${baseRoute}/${historyId}`, history);
  return data;
}

export function useEditArtworkHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editArtworkHistory,
    onSuccess: (_, { artworkId, historyId }) => {
      queryClient.invalidateQueries({
        queryKey: ["artwork-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["artwork", artworkId, "history"],
      });
    },
  });
}

async function deleteArtworkHistory({
  historyId,
}: {
  historyId: string;
  artworkId: string;
}) {
  await api.delete(`${baseRoute}/${historyId}`);
}

export function useDeleteArtworkHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteArtworkHistory,
    onSuccess: (_, { historyId, artworkId }) => {
      queryClient.invalidateQueries({
        queryKey: ["artwork-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["artwork", artworkId, "history"],
      });
    },
  });
}

async function updateHistorySequence({
  newSequence,
  artworkId,
}: {
  newSequence: string[];
  artworkId: string;
}) {
  await api.patch(`${artworkBaseRoute}/${artworkId}/history`, {
    sequence: newSequence,
  });
}

export const useUpdateHistorySequence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateHistorySequence,
    onSuccess: (_, { artworkId }) => {
      queryClient.invalidateQueries({
        queryKey: ["artwork", artworkId, "history"],
      });
    },
  });
};
